import { motion } from "framer-motion";
import { Calendar } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FormData } from "../types";

interface VideoDateStepProps {
  formData: FormData;
  onChange: (field: keyof FormData, value: string) => void;
}

const VideoDateStep = ({ formData, onChange }: VideoDateStepProps) => {
  return (
    <motion.div
      key="videoDate"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
          <Calendar className="w-8 h-8 text-primary" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-foreground mb-2">
          Memory Date
        </h2>
        <p className="text-muted-foreground">
          Optional: Would you like a date displayed in your slideshow?
        </p>
      </div>

      <div className="space-y-4">
        <Label>Select one</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => onChange("includeDateInVideo", "yes")}
            className={`p-4 rounded-xl border-2 transition-all text-center ${
              formData.includeDateInVideo === "yes"
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50"
            }`}
          >
            <span className="font-medium">I want to include a date</span>
          </button>
          <button
            type="button"
            onClick={() => onChange("includeDateInVideo", "no")}
            className={`p-4 rounded-xl border-2 transition-all text-center ${
              formData.includeDateInVideo === "no"
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50"
            }`}
          >
            <span className="font-medium">I do not want to include a date</span>
          </button>
        </div>
      </div>

      {formData.includeDateInVideo === "yes" && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="space-y-2"
        >
          <Label htmlFor="videoDate">Enter the date</Label>
          <Input
            id="videoDate"
            type="date"
            value={formData.videoDate}
            onChange={(e) => onChange("videoDate", e.target.value)}
          />
          <p className="text-sm text-muted-foreground">
            This date will appear in the slideshow video.
          </p>
        </motion.div>
      )}

      <div className="space-y-4 pt-4">
        <Label>When would you like to upload your photos?</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            type="button"
            onClick={() => onChange("uploadPhotosNow", "now")}
            className={`p-4 rounded-xl border-2 transition-all text-center ${
              formData.uploadPhotosNow === "now"
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50"
            }`}
          >
            <span className="font-medium">I will upload my photos now</span>
          </button>
          <button
            type="button"
            onClick={() => onChange("uploadPhotosNow", "later")}
            className={`p-4 rounded-xl border-2 transition-all text-center ${
              formData.uploadPhotosNow === "later"
                ? "border-primary bg-primary/5"
                : "border-border hover:border-primary/50"
            }`}
          >
            <span className="font-medium">I will upload my photos later</span>
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default VideoDateStep;
